<template>
  <div
    class="AppCard"
    :class="{ noGutter, feature, isLoading }"
    :style="computedAppCardStyle"
    @click="handleAppCardClick">
    <div
      class="_leading">
      <AppIcon
        :src="app.logo"
        :is-square="isLogoSquare"
      />

      <div
        class="pt-2">
        <h2
          class="AppCard_title">
          {{ app.title }}
          <BasePlaceholder
            v-if="!app.title"
            width="90%"
            height="24px"
          />
        </h2>
        <div
          v-if="isLoading || app.subTitle"
          class="AppCard_overview">
          {{ app.subTitle }}
          <div
            v-if="isLoading">
            <BasePlaceholder
              height="12px"
            />
            <BasePlaceholder
              class="mt-1"
              width="60%"
              height="12px"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="_trailing">
      <div
        class="AppCard_capabilities">
        <div
          v-if="isLoading"
          key="AppCard-Capabilities-Placeholder">
          <BasePlaceholder
            :blocks="3"
            width="24px"
            height="24px"
          />
        </div>

        <template
          v-if="!isLoading && computedCapabilities">
          <div
            class="AppCard_capability"
            v-for="capability in computedCapabilities"
            :key="capability">
            <i
              class="AppCard_capability_icon"
              :class="getCapabilityMap(capability).icon">
            </i>
            <span
              class="AppCard_capability_tooltip">
              {{ getCapabilityMap(capability).tooltip }}
            </span>
          </div>
        </template>

        <div
          v-if="!isLoading && app.priceDescription"
          class="AppCard_pricing"
          key="AppCard-Pricing">
          {{ app.priceDescription }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BasePlaceholder from '@/components/BasePlaceholder.vue';
import AppIcon from '@/components/AppIcon.vue';

const capabilityMap = {
  connector: {
    icon: 'icon-domo-connector',
    tooltip: 'Connector: Integrates data from another system into Domo',
  },
  dashboard: {
    icon: 'icon-domo-pages',
    tooltip: 'Dashboard: Pre-built reports, charts, and visualizations',
  },
  datascience: {
    icon: 'icon-domo-arrow-fork',
    tooltip: 'Data Science: Leverages advanced data science techniques',
  },
  dataflow: {
    icon: 'icon-domo-arrow-fork',
    tooltip: 'DataFlow: Cleans, transforms, calculates, and combines data from multiple data sources',
  },
  enterpriseapp: {
    icon: 'icon-domo-select-chart',
    tooltip: 'Enterprise App: Combines data, workflow, alerts, and action into a purpose-built solution',
  },
  writeback: {
    icon: 'icon-domo-wrench',
    tooltip: 'Writeback: Triggers data updates and changes in third party systems and databases',
  },
  federateddata: {
    icon: 'icon-domo-federated-data',
    tooltip: 'Federated Data: Connects directly to your company data for fast data with no duplication',
  },
  codeblock: {
    icon: 'icon-domo-code-tags',
    tooltip: 'Code Block: Pre-built blocks of code to extend and customize Domo',
  },
  dataset: {
    icon: 'icon-domo-database',
    tooltip: 'DataSet: 3rd party data easily accessible in Domo',
  },
};

export default {
  name: 'AppCard',

  components: {
    BasePlaceholder,
    AppIcon,
  },

  props: {
    app:          { type: Object,  default: null },
    isLoading:    { type: Boolean, default: true },
    noGutter:     { type: Boolean, default: false },
    feature:      { type: Boolean, default: false },
    img:          { type: String,  default: null },
    isLogoSquare: { type: Boolean, default: true },
  },

  computed: {
    computedAppCardStyle() {
      if (!this.feature || !this.img) return null;
      return {
        backgroundImage: `url(${this.img})`,
      };
    },

    computedCapabilities() {
      if (this.app.type === 'connector') {
        return ['Connector'];
      }

      if (!this.app.capability) return null;

      return this.app.capability
        .filter((capability) => (this.standardize(capability) in capabilityMap));
    },
  },

  methods: {
    handleAppCardClick() {
      if (this.isLoading) return;
      this.$emit('click', this.app);
    },

    getCapabilityMap(capability) {
      return capabilityMap[this.standardize(capability)];
    },

    standardize(text) {
      return text.replace(/\s/g, '').toLowerCase();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

$capability-icon-size: 24px;

._placeholder {
  border-radius: $radius;
  background-color: $grey;
}

.AppCard {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  height: 230px;
  padding: 24px;
  margin: 12px;
  font-size: 12px;
  background-color: $white;
  border-radius: 1px;
  box-shadow: 0 2px 8px 0 rgba($black, .1);
  cursor: pointer;
  &:hover {
    box-shadow: 0 2px 8px 4px rgba($black, .1);
  }
}

.AppCard.noGutter {
  margin: 0 !important;
}

.AppCard_title {
  margin-bottom: 8px;
  -webkit-line-clamp: 1;
}

.AppCard_title ._placeholder {
  height: 24px;
}

.AppCard_overview {
  -webkit-line-clamp: 2;
}

.AppCard_title,
.AppCard_overview {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.AppCard_capabilities {
  display: flex;
  align-items: center;
  margin-top: 16px;
  text-shadow: none;
  white-space: nowrap;
}

.AppCard_capability {
  position: relative;
  width: $capability-icon-size;
  height: $capability-icon-size;
  font-size: 18px;
  line-height: $capability-icon-size;
  color: rgba($black, .4);
  text-align: center;
  background-color: #EEE;
  & + .AppCard_capability,
  & + .AppCard_pricing {
    margin-left: 8px;
  }
}

.AppCard_capability_tooltip {
  $arrow-size: 6px;
  $left-bump: ($capability-icon-size * .5);
  display: none;
  position: absolute;
  width: 200px;
  bottom: ($capability-icon-size + $arrow-size);
  left: -$left-bump;
  padding: 8px;
  color: $white;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.4;
  white-space: normal;
  border-radius: $radius;
  background-color: rgba($black, .75);
  transform: translateX(0%);
  transition: display 0s ease .5s;
  &:after {
    content: '';
    position: absolute;
    left: $left-bump + $arrow-size;
    bottom: -$arrow-size;
    width: $arrow-size;
    border-left: $arrow-size solid transparent;
    border-right: $arrow-size solid transparent;
    border-top: $arrow-size solid rgba($black, .75);
  }
}

.AppCard_capability:hover .AppCard_capability_tooltip {
  display: block;
}

.AppCard_pricing {
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
}

// Featured
.AppCard.feature {
  height: 350px;
  color: $white;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 1px 1px rgba($black, .3);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  ._leading,
  ._trailing {
    z-index: 1;
    width: 80%;
    max-width: 400px;
  }
  .AppCard_title {
    -webkit-line-clamp: 2;
  }
  .AppCard_overview {
    -webkit-line-clamp: 4;
  }
  &:not(.isLoading):before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba($black, .4), rgba($black, .1));
  }
}

.AppCard.feature .AppCard_title {
  font-size: 30px;
}
</style>
